import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";

import { useActionHook, useModals } from "hooks";
import { uniq, uniqBy } from "lodash";
import { useAllFilesSelect, useDocumentsActions, useFoldersSelect } from "store";
import { IAiFile, IFile, IFileUploadType, IFolder } from "store/types";

import { usePickerFilters } from "./use-picker-filters";

export type ViewMode = "tree" | "list";

interface UseFilePickerSelectionProps {
  currentFiles: IAiFile[];
  isOpen: boolean;
  singleFileSelection?: boolean;
  dealId: number | null;
  getFilesOutsideDeal?: boolean;
  fileUploadType?: IFileUploadType[];
  attachedFiles?: File[];
  acceptedFilesExtensions?: string[];
}

interface SelectedFileItem {
  id: number;
  parentId: number | null;
}

export const useFilePickerSelection = ({
  currentFiles,
  isOpen,
  singleFileSelection = false,
  dealId,
  getFilesOutsideDeal = false,
  fileUploadType = [],
  attachedFiles: initialAttachments,
  acceptedFilesExtensions
}: UseFilePickerSelectionProps) => {
  const [selectedFiles, setSelectedFiles] = useState<SelectedFileItem[]>([]);
  const [collapsedFolderIds, setCollapsedFolderIds] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("tree");
  const {
    filters,
    hasActiveFilters,
    setClassifications,
    setInvestors,
    setEffectiveDate,
    clearFilter,
    clearAllFilters,
    resetFilters
  } = usePickerFilters();
  const [attachments, setAttachments] = useState<File[]>([]);
  const [oldFiles, setOldFiles] = useState<IAiFile[]>([]);
  const [deletedFilesIds, setDeletedFilesIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const { folders: childFolders, parentFolders } = useFoldersSelect({ kind: "folder" }).reduce(
    (acc, item) => {
      if (item.parent_id) {
        acc.folders.push(item);
      } else {
        acc.parentFolders.push(item);
      }
      return acc;
    },
    { folders: [] as IFolder[], parentFolders: [] as IFolder[] }
  );

  const { folders: dataroomChildFolders, parentFolders: dataroomParentFolders } = useFoldersSelect({
    kind: "dataroom"
  }).reduce(
    (acc, item) => {
      if (item.parent_id) {
        acc.folders.push(item);
      } else {
        acc.parentFolders.push(item);
      }
      return acc;
    },
    { folders: [] as IFolder[], parentFolders: [] as IFolder[] }
  );

  const aiFolders = useFoldersSelect({ kind: "ai-files" });
  const allStoreFiles = useAllFilesSelect();

  const allFolders = useMemo(
    () => [...childFolders, ...parentFolders, ...aiFolders, ...dataroomChildFolders],
    [childFolders, parentFolders, aiFolders, dataroomChildFolders]
  );

  const { errorModal } = useModals();
  const { getDealFoldersAction, getDealFilesAction } = useDocumentsActions();

  const {
    action: getDealFiles,
    loading: filesLoading,
    data: filesFromAPI
  } = useActionHook(getDealFilesAction, {
    onReject: () => errorModal({ msg: "Something went wrong" })
  });

  const {
    action: getDealFolders,
    loading: foldersLoading,
    data: foldersFromAPI
  } = useActionHook(getDealFoldersAction, {
    onSuccess: () => {
      if (dealId) getDealFiles(dealId);
    },
    onReject: () => errorModal({ msg: "Something went wrong" })
  });

  useEffect(() => {
    if (getFilesOutsideDeal && isOpen && dealId) {
      getDealFolders({ dealId });
    }
  }, [getFilesOutsideDeal, isOpen, dealId]);

  const resultFiles = useMemo(() => {
    // In outside-deal mode files always come from the API for the chosen deal
    // (empty until a deal is selected) — never fall back to the in-context store files.
    const files = getFilesOutsideDeal ? Object.values(filesFromAPI || {}) : allStoreFiles;

    return files.filter((file) => {
      const hasFileUrl = file.file_url;
      const hasSpecificTypes = fileUploadType.length ? fileUploadType.includes(file.upload_type) : true;
      return hasFileUrl && hasSpecificTypes;
    });
  }, [filesFromAPI, allStoreFiles, getFilesOutsideDeal, fileUploadType]);

  const resultFolders = useMemo(() => {
    const folders = getFilesOutsideDeal ? Object.values(foldersFromAPI || {}) : allFolders;
    return folders.filter((folder) => folder.kind === "folder" || folder.kind === "dataroom");
  }, [foldersFromAPI, allFolders, getFilesOutsideDeal]);

  const classificationOptions = useMemo(() => {
    const types = new Set<string>();
    resultFiles.forEach((f) => {
      if (f.document_type) types.add(f.document_type);
    });
    return Array.from(types).sort();
  }, [resultFiles]);

  const investorIdsInFiles = useMemo(() => {
    const ids = new Set<number>();
    resultFiles.forEach((f) => {
      if (f.investor_id) ids.add(f.investor_id);
    });
    return Array.from(ids);
  }, [resultFiles]);

  const matchesFilters = useCallback(
    (file: IFile): boolean => {
      if (filters.classifications.length) {
        if (!file.document_type || !filters.classifications.includes(file.document_type)) return false;
      }
      if (filters.investors.length) {
        if (!file.investor_id || !filters.investors.includes(file.investor_id)) return false;
      }
      if (filters.effectiveDate) {
        if (!file.date || !file.date.startsWith(filters.effectiveDate)) return false;
      }
      return true;
    },
    [filters]
  );

  const parentFoldersToRender = useMemo(() => {
    const isValidFolderKind = (folder: IFolder) => folder.kind === "folder" || folder.kind === "dataroom";
    const isRootFolder = (folder: IFolder) => folder.parent_id === null && isValidFolderKind(folder);

    if (getFilesOutsideDeal) {
      return Object.values(foldersFromAPI || {}).filter(isRootFolder);
    }

    if (dealId) {
      return [...parentFolders, ...dataroomParentFolders];
    }

    return Object.values(foldersFromAPI || {}).filter(isRootFolder);
  }, [dealId, parentFolders, dataroomParentFolders, foldersFromAPI, getFilesOutsideDeal]);

  // Preselect files when modal opens
  const preselectFiles = useCallback(() => {
    if (!currentFiles) return;
    const matchingFiles = resultFiles.filter((f) => currentFiles.some((cf) => cf.id === f.id));
    const availableFiles = getFilesOutsideDeal
      ? matchingFiles
      : matchingFiles.filter((f) => resultFolders.some((folder) => folder.id === f.section_id));

    setSelectedFiles((prev) => [
      ...prev,
      ...availableFiles
        .filter((f) => !prev.some((p) => p.id === f.id))
        .map((f) => ({ id: f.id, parentId: f.section_id }))
    ]);
    setOldFiles(currentFiles || []);
    setAttachments(initialAttachments || []);
  }, [currentFiles, resultFiles, resultFolders, getFilesOutsideDeal, initialAttachments]);

  useEffect(() => {
    if (isOpen && resultFiles.length && !initialized) {
      preselectFiles();
      setInitialized(true);
    }
  }, [isOpen, resultFiles.length, initialized]);

  useEffect(() => {
    if (currentFiles?.length) {
      setOldFiles(currentFiles);
    } else {
      setOldFiles([]);
    }
  }, [currentFiles]);

  // Auto-select parent folders based on selected files
  useEffect(() => {
    const findAllParents = (item: SelectedFileItem): SelectedFileItem[] => {
      const parentFolder = resultFolders.find((f) => f.id === item.parentId);
      const parents: SelectedFileItem[] = [];
      const collectParents = (folder: IFolder) => {
        parents.push({ id: folder.id, parentId: folder.parent_id });
        if (folder.parent_id) {
          const parent = resultFolders.find((f) => f.id === folder.parent_id);
          if (parent) collectParents(parent);
        }
      };
      if (parentFolder) collectParents(parentFolder);
      return parents;
    };

    const _allParents = uniqBy(selectedFiles.map(findAllParents).flat(), "id");
  }, [selectedFiles, resultFolders]);

  // Collapse all folders initially
  useEffect(() => {
    if (parentFoldersToRender.length && isOpen) {
      const allIds = new Set<number>();
      const collectIds = (folderId: number) => {
        allIds.add(folderId);
        resultFolders.filter((f) => f.parent_id === folderId).forEach((f) => collectIds(f.id));
      };
      parentFoldersToRender.forEach((f) => collectIds(f.id));
      setCollapsedFolderIds(allIds);
    }
  }, [parentFoldersToRender.length, isOpen]);

  const isFileSelected = useCallback(
    (id: number): boolean => selectedFiles.some((f) => f.id === id) || oldFiles.some((f) => f.id === id),
    [selectedFiles, oldFiles]
  );

  const toggleFile = useCallback(
    (id: number, parentId: number | null) => {
      if (singleFileSelection) {
        if (attachments.length > 0) return;

        if (!isFileSelected(id)) {
          setSelectedFiles([{ id, parentId }]);
          setDeletedFilesIds((prev) => prev.filter((fId) => fId !== id));
          setOldFiles([]);
        } else {
          setSelectedFiles([]);
          setOldFiles([]);
        }
      } else {
        if (!isFileSelected(id)) {
          setSelectedFiles((prev) => [...prev, { id, parentId }]);
          setDeletedFilesIds((prev) => prev.filter((fId) => fId !== id));
        } else {
          setSelectedFiles((prev) => prev.filter((f) => f.id !== id));
          setOldFiles((prev) => prev.filter((f) => f.id !== id));
        }
      }
    },
    [isFileSelected, singleFileSelection, attachments.length]
  );

  const getFilesInFolder = useCallback(
    (folderId: number): IFile[] => {
      const directFiles = resultFiles.filter((f) => f.section_id === folderId);
      const childFolderFiles = resultFolders
        .filter((f) => f.parent_id === folderId)
        .flatMap((f) => getFilesInFolder(f.id));
      return [...directFiles, ...childFolderFiles];
    },
    [resultFiles, resultFolders]
  );

  const selectAllInFolder = useCallback(
    (folderId: number) => {
      const folderFiles = getFilesInFolder(folderId);
      const areAllSelected = folderFiles.every((f) => isFileSelected(f.id));

      if (areAllSelected) {
        const fileIds = new Set(folderFiles.map((f) => f.id));
        setSelectedFiles((prev) => prev.filter((f) => !fileIds.has(f.id)));
        setOldFiles((prev) => prev.filter((f) => !fileIds.has(f.id)));
      } else {
        const newFiles = folderFiles
          .filter((f) => !isFileSelected(f.id))
          .map((f) => ({ id: f.id, parentId: f.section_id }));
        setSelectedFiles((prev) => [...prev, ...newFiles]);
      }
    },
    [getFilesInFolder, isFileSelected]
  );

  const isFolderAllSelected = useCallback(
    (folderId: number): boolean => {
      const folderFiles = getFilesInFolder(folderId);
      return folderFiles.length > 0 && folderFiles.every((f) => isFileSelected(f.id));
    },
    [getFilesInFolder, isFileSelected]
  );

  const isFolderIndeterminate = useCallback(
    (folderId: number): boolean => {
      const folderFiles = getFilesInFolder(folderId);
      const selectedCount = folderFiles.filter((f) => isFileSelected(f.id)).length;
      return selectedCount > 0 && selectedCount < folderFiles.length;
    },
    [getFilesInFolder, isFileSelected]
  );

  const toggleCollapse = useCallback((folderId: number) => {
    setCollapsedFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  }, []);

  const isCollapsed = useCallback(
    (folderId: number): boolean => collapsedFolderIds.has(folderId),
    [collapsedFolderIds]
  );

  const matchesSearch = useCallback(
    (file: IFile): boolean => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return file.name.toLowerCase().includes(q) || (file.document_type?.toLowerCase().includes(q) ?? false);
    },
    [searchQuery]
  );

  const folderHasMatchingFiles = useCallback(
    (folderId: number): boolean => {
      if (!searchQuery && !hasActiveFilters) return true;
      const folderFiles = getFilesInFolder(folderId);
      return folderFiles.some((f) => matchesSearch(f) && matchesFilters(f));
    },
    [searchQuery, hasActiveFilters, getFilesInFolder, matchesSearch, matchesFilters]
  );

  const filteredFileCount = useMemo(
    () => resultFiles.filter((f) => matchesSearch(f) && matchesFilters(f)).length,
    [resultFiles, matchesSearch, matchesFilters]
  );

  const resolvedSelectedFiles = useMemo((): IFile[] => {
    const selectedIds = new Set([...selectedFiles.map((f) => f.id), ...oldFiles.map((f) => f.id)]);
    const deleted = new Set(deletedFilesIds);
    return resultFiles.filter((f) => selectedIds.has(f.id) && !deleted.has(f.id));
  }, [selectedFiles, oldFiles, deletedFilesIds, resultFiles]);

  const selectedCount = useMemo(
    () => selectedFiles.length + attachments.length + oldFiles.filter((f) => !deletedFilesIds.includes(f.id)).length,
    [selectedFiles, attachments, oldFiles, deletedFilesIds]
  );

  const addFile = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const files = e.target?.files;
      if (!files) return;

      const validFiles = Array.from(files).filter((file) => {
        if (acceptedFilesExtensions?.length) {
          return acceptedFilesExtensions.some((ext) => file.name.toLowerCase().endsWith(ext.toLowerCase()));
        }
        const isValidMimeType = file.type.startsWith("image/") || file.type === "application/pdf";
        const isValidExtension =
          file.name.toLowerCase().endsWith(".pdf") || /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(file.name);
        return isValidMimeType || isValidExtension;
      });

      if (validFiles.length === 0) return;

      if (singleFileSelection) {
        setAttachments([validFiles[0]]);
        setSelectedFiles([]);
        setOldFiles([]);
      } else {
        setAttachments((prev) => [...prev, ...validFiles]);
      }

      e.target.value = "";
    },
    [singleFileSelection, acceptedFilesExtensions]
  );

  const removeAttachment = useCallback((file: File) => {
    const getKey = (f: File) => `${f.lastModified}-${f.name}-${f.size}`;
    setAttachments((prev) => prev.filter((f) => getKey(f) !== getKey(file)));
  }, []);

  const removeOldFile = useCallback((file: IAiFile) => {
    setOldFiles((prev) => prev.filter((f) => f.id !== file.id));
    setDeletedFilesIds((prev) => [...prev, file.id]);
  }, []);

  const removeFile = useCallback((id: number) => {
    setSelectedFiles((prev) => prev.filter((f) => f.id !== id));
    setOldFiles((prev) => prev.filter((f) => f.id !== id));
    setDeletedFilesIds((prev) => [...prev, id]);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedFiles([]);
    setOldFiles([]);
    setDeletedFilesIds([]);
    setAttachments([]);
  }, []);

  const resetState = useCallback(() => {
    setSelectedFiles([]);
    setAttachments([]);
    setDeletedFilesIds([]);
    setOldFiles([]);
    setSearchQuery("");
    setViewMode("tree");
    resetFilters();
    setInitialized(false);
    setCollapsedFolderIds(new Set());
  }, [resetFilters]);

  const getSubmitData = useCallback(() => {
    const fileIdsFromSelection = selectedFiles.map((f) => f.id);
    const oldFileIds = oldFiles.map((f) => f.id);
    const filesIds = uniq([...fileIdsFromSelection, ...oldFileIds]).filter((id) => !deletedFilesIds.includes(id));

    return {
      filesIds,
      attachments,
      files: resolvedSelectedFiles
    };
  }, [selectedFiles, oldFiles, deletedFilesIds, attachments, resolvedSelectedFiles]);

  const isSelectionDisabled = useCallback(
    (id?: number) => {
      if (!singleFileSelection) return false;
      if (attachments.length > 0) return true;
      if (id !== undefined && selectedFiles.some((f) => f.id === id)) return false;
      return selectedFiles.length > 0;
    },
    [singleFileSelection, attachments.length, selectedFiles]
  );

  const totalFileCount = useMemo(() => resultFiles.length, [resultFiles]);

  return {
    // Selection
    selectedFiles,
    isFileSelected,
    toggleFile,
    selectAllInFolder,
    isFolderAllSelected,
    isFolderIndeterminate,
    resolvedSelectedFiles,
    selectedCount,
    clearSelection,
    removeFile,
    isSelectionDisabled,

    // Collapse
    toggleCollapse,
    isCollapsed,
    collapsedFolderIds,

    // Search & view
    searchQuery,
    setSearchQuery,
    viewMode,
    setViewMode,
    matchesSearch,
    matchesFilters,
    folderHasMatchingFiles,

    // Filters
    filters,
    hasActiveFilters,
    classificationOptions,
    investorIdsInFiles,
    filteredFileCount,
    setClassifications,
    setInvestors,
    setEffectiveDate,
    clearFilter,
    clearAllFilters,

    // File management
    attachments,
    addFile,
    removeAttachment,
    oldFiles,
    removeOldFile,
    deletedFilesIds,

    // Data
    resultFiles,
    resultFolders,
    parentFoldersToRender,
    totalFileCount,

    // Loading
    loading,
    setLoading,
    foldersLoading,
    filesLoading,

    // Actions
    resetState,
    getSubmitData,

    // Config
    singleFileSelection,
    acceptedFilesExtensions
  };
};

export type FilePickerSelectionReturn = ReturnType<typeof useFilePickerSelection>;
